import type { SwAuthDebugBufferedEvent } from './swAuthDebugBuffer.ts'
import { formatSwAuthTelemetryEvent } from './swAuthDebugBuffer.ts'
import type { SwAuthSyncMetricSnapshot } from './swAuthSyncMetricsCore'
import { RELOAD_LOOP_MIN_LOADS, RELOAD_LOOP_WINDOW_MS } from './reloadLoopMonitor'

const LOAD_TIMESTAMPS_KEY = 'gh_page_load_timestamps_v1'
const LOOP_REPORTED_KEY = 'gh_reload_loop_reported_v1'
const LAST_RELOAD_INTENT_KEY = 'gh_last_reload_intent_v1'

function readSessionJson(key: string): unknown {
  try {
    const raw = sessionStorage.getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export type SwAuthDebugSnapshotInput = {
  mainUid: string | null
  mainIssuedAtMs: number | null
  idbUid: string | null
  idbReadError: string | null
  swRegistered: boolean
  swScriptUrl: string
  swControllerState: string | null
  swActiveState: string | null
  swWaitingState: string | null
  swInstallingState: string | null
  hasRegistration: boolean
  metrics: SwAuthSyncMetricSnapshot
  telemetryEvents: readonly SwAuthDebugBufferedEvent[]
  consoleLines: readonly string[]
}

/** Plain-text report for clipboard / support tickets. */
export function getReloadLoopSessionInfo(input: SwAuthDebugSnapshotInput): string {
  const lines: string[] = [
    '=== GH Webapp SW / Auth debug ===',
    `capturedAt: ${new Date().toISOString()}`,
    `url: ${typeof window !== 'undefined' ? window.location.href : ''}`,
    `crossOriginIsolated: ${typeof window !== 'undefined' ? window.crossOriginIsolated : 'n/a'}`,
    `displayMode: ${typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(display-mode: standalone)').matches : 'n/a'}`,
    '',
    '--- Main auth ---',
    `mainUid: ${input.mainUid ?? '(none)'}`,
    `mainIssuedAtMs: ${input.mainIssuedAtMs ?? '(none)'}`,
    `idbPersistedUid: ${input.idbReadError ? `(read error: ${input.idbReadError})` : (input.idbUid ?? '(none)')}`,
    `uidMatch: ${input.mainUid && input.idbUid ? input.mainUid === input.idbUid : 'n/a'}`,
    '',
    '--- Service worker ---',
    `swRegistered: ${input.swRegistered}`,
    `hasRegistration: ${input.hasRegistration}`,
    `swScriptUrl: ${input.swScriptUrl}`,
    `swController: ${input.swControllerState ?? 'none'}`,
    `swActive: ${input.swActiveState ?? 'none'}`,
    `swWaiting: ${input.swWaitingState ?? 'none'}`,
    `swInstalling: ${input.swInstallingState ?? 'none'}`,
    '',
    '--- SW auth metrics (session cumulative) ---',
    ...Object.entries(input.metrics).map(([k, v]) => `${k}: ${v}`),
    '',
    '--- Reload loop monitor ---',
    `windowMs: ${RELOAD_LOOP_WINDOW_MS}`,
    `minLoads: ${RELOAD_LOOP_MIN_LOADS}`,
    `loadTimestamps: ${JSON.stringify(readSessionJson(LOAD_TIMESTAMPS_KEY))}`,
    `loopReported: ${typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(LOOP_REPORTED_KEY) : 'n/a'}`,
    `lastReloadIntent: ${JSON.stringify(readSessionJson(LAST_RELOAD_INTENT_KEY))}`,
    '',
    '--- SW auth telemetry (oldest → newest) ---',
  ]

  if (input.telemetryEvents.length === 0) {
    lines.push('(none)')
  } else {
    for (const row of input.telemetryEvents) {
      lines.push(`${row.atLabel} ${formatSwAuthTelemetryEvent(row.event)}`)
    }
  }

  lines.push('', '--- Captured console ---')
  if (input.consoleLines.length === 0) {
    lines.push('(none)')
  } else {
    lines.push(...input.consoleLines)
  }

  return lines.join('\n')
}
